import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api.js';
import type { IngestedImage } from '../../lib/types.js';
import { StatusBadge } from '../../components/StatusBadge.js';

export function IngestedImagesPage() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['ingested-images'],
    queryFn: () => api.get<IngestedImage[]>('/ingested-images?pageSize=100'),
  });

  const upload = useMutation({
    mutationFn: async () => {
      const file = fileInputRef.current?.files?.[0];
      if (!file) throw new Error('Please select a PNG file');
      const form = new FormData();
      form.append('file', file);
      form.append('originalFilename', file.name);
      if (note.trim()) form.append('note', note.trim());
      return api.upload<IngestedImage>('/ingested-images', form);
    },
    onSuccess: async () => {
      setNote('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploadError(null);
      await queryClient.invalidateQueries({ queryKey: ['ingested-images'] });
    },
    onError: (err) => setUploadError(err instanceof ApiClientError ? err.message : String(err)),
  });

  const images = listQuery.data?.data ?? [];

  return (
    <section>
      <h1>Ingested Images</h1>
      <p className="muted">
        Upload manually-created PNG artwork from ChatGPT for transparency analysis and review.
      </p>

      <div className="card">
        <h2>Upload PNG</h2>
        <div className="form-row">
          <label htmlFor="png-file">PNG file</label>
          <input id="png-file" type="file" accept="image/png" ref={fileInputRef} />
        </div>
        <div className="form-row">
          <label htmlFor="upload-note">Note (optional)</label>
          <input
            id="upload-note"
            type="text"
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. ChatGPT session date or prompt description"
          />
        </div>
        {uploadError && (
          <p role="alert" className="form-error">
            {uploadError}
          </p>
        )}
        <button type="button" onClick={() => upload.mutate()} disabled={upload.isPending}>
          {upload.isPending ? 'Uploading…' : 'Upload'}
        </button>
      </div>

      {images.length === 0 && !listQuery.isLoading && (
        <p className="muted">No ingested images yet.</p>
      )}

      <div className="card-grid">
        {images.map((img) => (
          <Link key={img.id} to={`/ingested-images/${img.id}`} className="asset-card">
            <img src={`/api/v1/ingested-images/${img.id}/original/content`} alt="ingested" />
            <StatusBadge status={img.status} />
            <span className="muted">{img.provenance.originalFilename}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}
